"use client";

import { Suspense } from "react";
import { motion } from "framer-motion";
import { ContactForm } from "@/components/ContactForm";
import { CONTACT_EMAIL } from "@/lib/constants";
import { useTranslation } from "@/lib/i18n";
import { SOFIA_CONNECT_ASN_LABEL } from "@/lib/network-metrics";

export function ContactPageClient() {
  const { t } = useTranslation();

  const contactChannels = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
        </svg>
      ),
      label: t("contact_channel_sales"),
      value: CONTACT_EMAIL.sales,
      href: `mailto:${CONTACT_EMAIL.sales}`,
      color: "#0066ff",
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      label: t("contact_channel_noc"),
      value: CONTACT_EMAIL.noc,
      href: `mailto:${CONTACT_EMAIL.noc}`,
      color: "#00d4aa",
      live: true,
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
        </svg>
      ),
      label: t("contact_channel_phone"),
      value: "+359 2 420 0300",
      href: "tel:+35924200300",
      color: "#0066ff",
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
        </svg>
      ),
      label: t("contact_channel_office"),
      value: "192A Cherni Vrah Blvd, Sofia 1407",
      href: "https://maps.app.goo.gl/Xk5tFFVRBwSgi6gV6",
      color: "#00d4aa",
    },
  ];

  return (
    <div className="pt-24 pb-16">
      {/* ── Hero ──
          SC-PERF (mobile LCP): the hero block was previously wrapped in a
          motion.div with initial={{ opacity: 0, y: 20 }}. Lighthouse measures
          LCP only once the element reaches its final paint state, so the
          fade-in pushed the production /contact LCP to ~4.8s on mobile. The
          motion wrapper has been removed; the h1 paints with the rest of the
          shell. Sub-fold sections (channels, form, map) keep their enter
          animations because they are not LCP candidates. */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16">
        <div className="mx-auto max-w-7xl text-center">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-eyebrow">
              {t("contact_eyebrow")}
            </span>
            <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-foreground">
              {t("contact_hero_title")}
            </h1>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-muted-foreground">
              {t("contact_hero_subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* ── Contact Channels strip ── */}
      <section className="px-4 sm:px-6 lg:px-8 pb-12">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {contactChannels.map((ch, i) => (
              <motion.a
                key={ch.label}
                href={ch.href}
                target={ch.href.startsWith("http") ? "_blank" : undefined}
                rel={ch.href.startsWith("http") ? "noopener noreferrer" : undefined}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.05, duration: 0.4 }}
                className="bento-card group flex items-start gap-3 hover:border-primary/30"
              >
                <div
                  className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${ch.color}15`, color: ch.color }}
                >
                  {ch.icon}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {ch.label}
                    </span>
                    {ch.live && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-accent/20 text-accent-text">
                        <span className="w-1 h-1 rounded-full bg-accent animate-pulse" />
                        {t("contact_channel_live")}
                      </span>
                    )}
                  </div>
                  <span className="mt-0.5 block text-sm text-foreground group-hover:text-primary-light transition-colors">
                    {ch.value}
                  </span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Form + Map grid ── */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Suspense fallback={<div className="bento-card animate-pulse h-96" />}>
                <ContactForm />
              </Suspense>
            </motion.div>

            {/* Right: Map + Office info */}
            <div className="space-y-6">
              {/* Office details card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="bento-card"
              >
                <div className="relative z-10">
                  <h2 className="text-base font-semibold text-foreground mb-4">
                    {t("contact_office_details")}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground mb-1">{t("contact_office_legal_name")}</p>
                      <p className="text-foreground font-medium">Sofia Connect EAD</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">{t("contact_office_asn")}</p>
                      <p className="font-mono text-foreground font-medium">{SOFIA_CONNECT_ASN_LABEL}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">{t("contact_office_hours")}</p>
                      <p className="text-foreground font-medium">{t("contact_office_hours_value")}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">{t("contact_office_noc_availability")}</p>
                      <p className="text-foreground font-medium flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                        24/7/365
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
